import { CheckYesIcon, HandRaiseIcon, ProfilePhoto, XNoIcon } from "@minervaproject/fds";
import { unwrapResult } from "@reduxjs/toolkit";
import clsx from "clsx";
import cuid from "cuid";
import { cloneDeep } from "lodash";
import { useEffect, useRef, useState } from "react";

import { REACTION_ICONS_MAP, VOTE_ICONS_MAP } from "components/shared/signals_and_reactions";
import { createNoClassroomChangeToast } from "components/shared/toasts";
import { useCapability, useUserIdsWithCapability } from "hooks/capabilities";
import { useAppDispatch, useAppSelector } from "hooks/redux_hooks";
import { useUsersProperties } from "hooks/use_user_properties";
import { addUserToParticipantBarAndToBestPaneOnStage } from "shared/reducers/classroom";
import { CapabilityType } from "shared/types";
import { ReactionType, UserType, VoteType } from "shared/types/user";
import { classroomSelectors } from "store";
import { computeInitials, profilePhotoUrl } from "utils/user";

import "./reaction_feed.scss";

type PossibleFeedReactions = Exclude<ReactionType | VoteType | "hand-raised", null>;
interface ReactionStackType {
  userId: string;
  reaction: PossibleFeedReactions;
  createdAt: number;
  id: string;
  isFading: boolean;
  isStackOf?: number;
}

const MAX_STACK_SIZE = 5;
const MAX_SAME_REACTION_COUNT = 3;
const FADE_DURATION_MS = 500;
const REACTION_TIMEOUT_MS = 6000;

function ReactionObserver() {
  const isPresentationView = useAppSelector(classroomSelectors.selectIsPresentationView());
  const activeBreakoutSessionId = useAppSelector(
    classroomSelectors.selectActiveBreakoutSessionId(),
  );

  if (activeBreakoutSessionId) {
    return null;
  }

  return (
    <div className="absolute left-0 bottom-0 ml4 mb4" style={{ zIndex: 9999 }}>
      <ReactionFeed />
      {isPresentationView && <VoteRollup />}
    </div>
  );
}

function ReactionFeed() {
  const dispatch = useAppDispatch();
  const canControlClassroom = useCapability(CapabilityType.CAN_CONTROL_CLASSROOM);
  const isInPerson = useAppSelector(classroomSelectors.selectIsInPersonClass());
  const isPresentationView = useAppSelector(classroomSelectors.selectIsPresentationView());
  const participantBarUserIds = useAppSelector(classroomSelectors.selectParticipantBarUserIds());
  const isVisibleInClassUserIds = useUserIdsWithCapability(CapabilityType.IS_VISIBLE_IN_CLASS);
  const users = useUsersProperties(["vote", "reaction", "is_hand_raised"]);
  const [previousUsers, setPreviousUsers] = useState<UserType[]>([]);
  const [reactionStack, setReactionStack] = useState<ReactionStackType[]>([]);
  const userList = Object.values(users).filter((u) => isVisibleInClassUserIds.includes(u.id));
  const timeout1 = useRef<NodeJS.Timeout>();
  const timeout2 = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (!previousUsers.length && userList.length) {
      setPreviousUsers(cloneDeep(userList));
      return;
    }

    const newReactionStack = userList
      .map((u) => {
        const previousUser = previousUsers.find((pu) => pu.id === u.id);
        const userReactions: ReactionStackType[] = reactionStack.filter((r) => r.userId === u.id);
        if (!previousUser) {
          return [];
        }

        const id = cuid();
        let newReaction: PossibleFeedReactions | null = null;
        const shouldShowVotes = !isPresentationView;
        if (u.vote && previousUser.vote !== u.vote && shouldShowVotes) {
          newReaction = u.vote;
        }
        if (u.reaction && previousUser.reaction !== u.reaction) {
          newReaction = u.reaction;
        }
        if (u.is_hand_raised && previousUser.is_hand_raised !== u.is_hand_raised) {
          newReaction = "hand-raised";
        }

        const userInParticipantBar = participantBarUserIds.includes(u.id);
        const userReactionInStackAlready = userReactions.find((r) => r.reaction === newReaction);
        if (!newReaction || (!isInPerson && userInParticipantBar) || userReactionInStackAlready) {
          return userReactions;
        }

        timeout1.current = setTimeout(
          () =>
            setReactionStack((prevState) =>
              prevState.map((r) => (r.id === id ? { ...r, isFading: true } : r)),
            ),
          REACTION_TIMEOUT_MS,
        );
        timeout2.current = setTimeout(
          () => setReactionStack((prevState) => prevState.filter((r) => r.id !== id)),
          REACTION_TIMEOUT_MS + FADE_DURATION_MS,
        );
        return [
          ...userReactions,
          { userId: u.id, reaction: newReaction, id, createdAt: Date.now(), isFading: false },
        ];
      })
      .flat();

    // Sorting makes sure that order is preserved
    newReactionStack.sort((a, b) => a.createdAt - b.createdAt);

    if (
      newReactionStack.length !== reactionStack.length ||
      newReactionStack.some((r, i) => r.reaction !== reactionStack[i].reaction)
    ) {
      const l = newReactionStack.length;
      setReactionStack(newReactionStack.slice(l - MAX_STACK_SIZE < 0 ? 0 : l - MAX_STACK_SIZE, l));
    }

    const previousUserListKey = previousUsers
      .map((u) => [u.reaction, u.vote, u.is_hand_raised].join("-"))
      .join(",");
    const userListKey = userList
      .map((u) => [u.reaction, u.vote, u.is_hand_raised].join("-"))
      .join(",");
    if (previousUserListKey !== userListKey) {
      setPreviousUsers(userList);
    }
  }, [
    participantBarUserIds,
    previousUsers,
    userList,
    reactionStack,
    isPresentationView,
    isInPerson,
  ]);

  useEffect(() => {
    return () => {
      clearTimeout(timeout1.current);
      clearTimeout(timeout2.current);
    };
  }, []);

  const condensedStack = reactionStack
    .map((r, i, stack) => {
      const restOfStack = stack.slice(i + 1);
      const similarReactions = restOfStack.filter((r2) => r2.reaction === r.reaction);
      if (similarReactions.length >= MAX_SAME_REACTION_COUNT) {
        return { ...r, isStackOf: similarReactions.length };
      }
      return r;
    })
    .reduce((acc, r) => {
      if (acc.some((r2) => r2.reaction === r.reaction && r2.isStackOf)) {
        return acc;
      }
      return [...acc, r];
    }, [] as ReactionStackType[]);

  return (
    <div data-testid="reaction-feed">
      {condensedStack.map((reaction) => {
        if (reaction.reaction === null) {
          return null;
        }
        const user = users[reaction.userId];
        const ReactionIcon =
          REACTION_ICONS_MAP[reaction.reaction] ||
          VOTE_ICONS_MAP[reaction.reaction] ||
          HandRaiseIcon;
        const iconSize = isPresentationView ? 48 : 32;
        return (
          <div
            key={`reaction-${reaction.id}`}
            className={clsx(
              "bg-white shadow-level-1 p1 mt2 relative feed-item flex items-center profile-photo-overlap",
              {
                fading: reaction.isFading,
                "cursor-pointer": canControlClassroom && !isInPerson,
              },
            )}
            onClick={
              canControlClassroom && !isInPerson
                ? async () => {
                    const actionResult = await dispatch(
                      addUserToParticipantBarAndToBestPaneOnStage({ userId: user.id }),
                    );
                    const result = unwrapResult(actionResult);
                    if (!result.updated) createNoClassroomChangeToast(result.msg);
                  }
                : undefined
            }
            data-testid={`reaction-feed-item-${user.id}-${reaction.reaction}`}
          >
            <ReactionIcon width={iconSize} height={iconSize} />
            <ProfilePhoto
              style={{ marginLeft: "-8px" }}
              size={isPresentationView ? "lg" : "md"}
              src={profilePhotoUrl(user)}
              initials={computeInitials(user)}
            />
            <span className="mx2 body">
              {user.first_name}
              {reaction.isStackOf && ` and ${reaction.isStackOf} others`}
              {reaction.reaction === "hand-raised" && " raised their hand"}{" "}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function VoteRollup() {
  const users = Object.values(useUsersProperties(["is_present", "vote"]));
  const canParticipateInClassUserIds = useUserIdsWithCapability(
    CapabilityType.CAN_PARTICIPATE_IN_CLASS,
  );
  const students = users.filter(({ id }) => canParticipateInClassUserIds.includes(id));
  const presentStudents = students.filter(({ is_present }) => is_present);
  const yesCount = presentStudents.filter(({ vote }) => vote === "yes").length;
  const noCount = presentStudents.filter(({ vote }) => vote === "no").length;
  const totalCount = presentStudents.length;

  if (yesCount === 0 && noCount === 0) {
    return null;
  }

  return (
    <div className="bg-white vote-rollup--presentation px2 py1 mt2">
      <div className="flex items-center px2 mb1">
        <CheckYesIcon width={16} height={16} />
        <div className="mx2 vote-rollup-bar flex content-start yes">
          <div
            className="vote-rollup-bar-content yes"
            style={{ width: `${(100 * yesCount) / totalCount}%` }}
          />
        </div>
        <span className="body text-black-tint-70 count" style={{ marginBottom: "2px" }}>
          {yesCount}
        </span>
      </div>
      <div className="flex items-center px2">
        <XNoIcon width={16} height={16} />
        <div className="mx2 vote-rollup-bar flex content-start no">
          <div
            className="vote-rollup-bar-content no"
            style={{ width: `${(100 * noCount) / totalCount}%` }}
          />
        </div>
        <span className="body text-black-tint-70 count" style={{ marginBottom: "2px" }}>
          {noCount}
        </span>
      </div>
    </div>
  );
}

export { ReactionObserver, ReactionFeed, VoteRollup };
